import React, { useState, useEffect, useCallback } from 'react';
import { aiAPI } from '../utils/api';
import { History, Search, Users, Building2, Sparkles, Trash2, Loader2, Calendar, Filter } from 'lucide-react';

var typeLabels = {
  find_contacts: { label: 'Find Contacts', icon: Users, color: 'text-blue-600 bg-blue-50' },
  bulk_find_contacts: { label: 'Bulk PIC Scan', icon: Users, color: 'text-purple-600 bg-purple-50' },
  discover_organizations: { label: 'Discover Orgs', icon: Building2, color: 'text-green-600 bg-green-50' },
  enrich_organization: { label: 'Enrich', icon: Sparkles, color: 'text-amber-600 bg-amber-50' },
};

function AIHistory() {
  var s1 = useState([]); var items = s1[0]; var setItems = s1[1];
  var s2 = useState(true); var loading = s2[0]; var setLoading = s2[1];
  var s3 = useState(0); var total = s3[0]; var setTotal = s3[1];
  var s4 = useState(null); var filter = s4[0]; var setFilter = s4[1];
  var s5 = useState(''); var dateFrom = s5[0]; var setDateFrom = s5[1];
  var s6 = useState(''); var dateTo = s6[0]; var setDateTo = s6[1];
  var s7 = useState(0); var minResults = s7[0]; var setMinResults = s7[1];
  var s8 = useState(''); var searchQuery = s8[0]; var setSearchQuery = s8[1];
  var s9 = useState(false); var showFilters = s9[0]; var setShowFilters = s9[1];

  var fetchHistory = useCallback(async function() {
    setLoading(true);
    try {
      var params = { limit: 100 };
      if (filter) params.search_type = filter;
      var res = await aiAPI.history(params);
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(function() { fetchHistory(); }, [fetchHistory]);

  var handleDelete = async function(id) {
    try {
      await aiAPI.deleteHistory(id);
      setItems(function(prev) { return prev.filter(function(i) { return i._id !== id; }); });
      setTotal(function(p) { return p - 1; });
    } catch (e) { console.error(e); }
  };

  var handleClearAll = async function() {
    if (!window.confirm('Clear all AI search history?')) return;
    try {
      await aiAPI.clearHistory();
      setItems([]);
      setTotal(0);
    } catch (e) { console.error(e); }
  };

  var formatDate = function(d) {
    if (!d) return '';
    return new Date(d).toLocaleString('en-MY', { dateStyle: 'medium', timeStyle: 'short' });
  };

  // Client-side filtering for date range, min results, and text search
  var filteredItems = items.filter(function(item) {
    if (dateFrom) {
      var fromDate = new Date(dateFrom);
      var itemDate = new Date(item.created_date);
      if (itemDate < fromDate) return false;
    }
    if (dateTo) {
      var toDate = new Date(dateTo);
      toDate.setHours(23, 59, 59);
      var itemDate2 = new Date(item.created_date);
      if (itemDate2 > toDate) return false;
    }
    if (minResults > 0 && (item.result_count || 0) < minResults) return false;
    if (searchQuery) {
      var q = searchQuery.toLowerCase();
      var query = item.query || {};
      var queryText = (query.organization || query.query || query.client_name || '').toLowerCase();
      if (!queryText.includes(q)) return false;
    }
    return true;
  });

  var stats = {};
  for (var si = 0; si < items.length; si++) {
    var type = items[si].search_type;
    stats[type] = (stats[type] || 0) + 1;
  }

  return (
    <div className="p-3 sm:p-6 space-y-5" data-testid="ai-history-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900">AI Search History</h1>
          <p className="text-sm text-slate-500">{total} total searches logged</p>
        </div>
        <div className="flex gap-2">
          <button onClick={function() { setShowFilters(!showFilters); }} className={'px-3 py-2 text-sm border rounded-lg flex items-center gap-2 transition-colors ' + (showFilters ? 'bg-primary text-white border-primary' : 'border-slate-200 text-slate-600 hover:bg-slate-50')} data-testid="toggle-filters-btn">
            <Filter size={14} /> Filters
          </button>
          {total > 0 && (
            <button onClick={handleClearAll} className="px-3 py-2 text-sm text-red-600 border border-red-200 rounded-lg hover:bg-red-50 flex items-center gap-2" data-testid="clear-history-btn">
              <Trash2 size={14} /> Clear All
            </button>
          )}
        </div>
      </div>

      {/* Type filter chips */}
      <div className="flex gap-2 flex-wrap">
        {[null, 'find_contacts', 'discover_organizations', 'enrich_organization', 'bulk_find_contacts'].map(function(f) {
          var label = f ? (typeLabels[f] ? typeLabels[f].label : f) : 'All';
          var count = f ? (stats[f] || 0) : total;
          return (
            <button key={f || 'all'} onClick={function() { setFilter(f); }} className={'px-3 py-1.5 rounded-lg text-sm border transition-colors flex items-center gap-1.5 ' + (filter === f ? 'bg-primary text-white border-primary' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50')} data-testid={'filter-' + (f || 'all')}>
              {label} <span className={'text-xs rounded-full px-1.5 ' + (filter === f ? 'bg-white/20' : 'bg-slate-100')}>{count}</span>
            </button>
          );
        })}
      </div>

      {/* Advanced Filters */}
      {showFilters && (
        <div className="bg-white rounded-lg border border-slate-200 p-4 grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="advanced-filters">
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Search Query</label>
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input type="text" value={searchQuery} onChange={function(e) { setSearchQuery(e.target.value); }} placeholder="Search in queries..." className="w-full pl-8 pr-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="history-search-input" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">From Date</label>
            <input type="date" value={dateFrom} onChange={function(e) { setDateFrom(e.target.value); }} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="date-from-input" />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">To Date</label>
            <input type="date" value={dateTo} onChange={function(e) { setDateTo(e.target.value); }} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="date-to-input" />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Min Results</label>
            <select value={minResults} onChange={function(e) { setMinResults(parseInt(e.target.value)); }} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="min-results-filter">
              <option value={0}>Any</option>
              <option value={1}>1+</option>
              <option value={3}>3+</option>
              <option value={5}>5+</option>
              <option value={10}>10+</option>
            </select>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 size={24} className="animate-spin text-primary" /></div>
      ) : filteredItems.length === 0 ? (
        <div className="bg-white rounded-lg border border-slate-200 p-12 text-center">
          <History size={40} className="text-slate-200 mx-auto mb-3" />
          <p className="text-slate-500">{items.length > 0 ? 'No results match your filters' : 'No AI search history yet'}</p>
          <p className="text-xs text-slate-400 mt-1">
            {items.length > 0 ? 'Try adjusting your filter criteria' : 'History is logged when you use AI features like Find Contacts or Discover Organisations'}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-slate-200 divide-y divide-slate-100">
          <div className="px-4 py-2 bg-slate-50 border-b border-slate-200">
            <p className="text-xs text-slate-500">Showing {filteredItems.length} of {items.length} entries</p>
          </div>
          {filteredItems.map(function(item) {
            var typeInfo = typeLabels[item.search_type] || { label: item.search_type, icon: Search, color: 'text-slate-600 bg-slate-50' };
            var Icon = typeInfo.icon;
            var query = item.query || {};
            var queryStr = query.organization || query.query || query.client_name || JSON.stringify(query).slice(0, 80);
            return (
              <div key={item._id} className="flex items-center gap-4 px-4 py-3 hover:bg-slate-50 transition-colors" data-testid={'history-item-' + item._id}>
                <div className={'w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ' + typeInfo.color}>
                  <Icon size={14} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-slate-900">{typeInfo.label}</span>
                    <span className="text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-500">{item.result_count} results</span>
                  </div>
                  <p className="text-xs text-slate-500 truncate mt-0.5">{queryStr}</p>
                </div>
                <span className="text-xs text-slate-400 flex-shrink-0">{formatDate(item.created_date)}</span>
                <button onClick={function() { handleDelete(item._id); }} className="text-slate-300 hover:text-red-400 flex-shrink-0" data-testid={'delete-history-' + item._id}>
                  <Trash2 size={14} />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default AIHistory;
